import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import api from '../../lib/api';
import {
  LayoutDashboard,
  Briefcase,
  BarChart3,
  FileText,
  Video,
  Bell,
  User,
  Users,
  LogOut,
  ScrollText,
  FileDown,
  Wallet,
  PanelLeftClose,
  PanelLeft,
  Palette,
  UserCog,
} from 'lucide-react';
import ThemeToggle from '../ThemeToggle';
import OpenKanbanButton from '../OpenKanbanButton';
import KolMyGlassLogo from '../KolMyGlassLogo';
import AppearanceSettings from '../AppearanceSettings';
import { useDesktopSidebarHidden } from '../../hooks/useDesktopSidebarHidden';
import { portalShellProps } from '../../lib/appearance';
import { portalContentClassName } from '../../lib/portalContent';
import {
  PortalNavGroups,
  flatNavFromGroups,
  navLinkBase,
  navLinkInactive,
  type NavGroup,
} from './PortalNav';
import { PortalMobileTopBar } from './PortalMobileTopBar';
import { PortalUserChip } from './PortalUserChip';

export default function BrandLayout() {
  const { logout, user } = useAuthStore();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();
  const location = useLocation();
  const [pendingCounts, setPendingCounts] = useState({ jobs: 0, scripts: 0, submissions: 0, invitations: 0 });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const { isDesktopSidebarHidden, setIsDesktopSidebarHidden } = useDesktopSidebarHidden();

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    fetchPendingCounts();
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchPendingCounts();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, user?.id]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const fetchPendingCounts = async () => {
    try {
      const response = await api.get('/pending-counts');
      setPendingCounts(response.data);
    } catch {
      // Silently fail
    }
  };

  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        label: 'Overview',
        items: [
          { path: '/brand/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
          { path: '/brand/analytics', icon: BarChart3, label: 'Analytics' },
        ],
      },
      {
        label: 'Campaigns',
        items: [
          { path: '/brand/jobs', icon: Briefcase, label: 'Jobs' },
          { path: '/brand/contracts', icon: ScrollText, label: 'Contracts' },
          { path: '/brand/payments', icon: Wallet, label: 'Payments' },
          { path: '/brand/reports', icon: FileDown, label: 'Reports' },
          {
            path: '/brand/scripts',
            icon: FileText,
            label: 'Scripts',
            badge: pendingCounts.scripts > 0 ? pendingCounts.scripts : undefined,
          },
          {
            path: '/brand/submissions',
            icon: Video,
            label: 'Submissions',
            badge: pendingCounts.submissions > 0 ? pendingCounts.submissions : undefined,
          },
        ],
      },
      {
        label: 'Talent',
        items: [{ path: '/brand/kols', icon: Users, label: 'KOLs' }],
      },
    ],
    [pendingCounts.scripts, pendingCounts.submissions]
  );

  const allNavItems = flatNavFromGroups(navGroups);
  const pageTitle =
    /^\/brand\/scripts\/.+/.test(location.pathname)
      ? 'Script studio'
      : allNavItems.find((item) => location.pathname.startsWith(item.path))?.label || 'Brand';

  const shell = portalShellProps('brand');

  return (
    <div
      className="theme-brand glass-mesh-bg flex h-screen text-glass-text font-sans"
      {...shell}
    >
      <PortalMobileTopBar
        menuOpen={isMobileMenuOpen}
        onToggleMenu={() => setIsMobileMenuOpen((open) => !open)}
        notificationsTo="/brand/notifications"
        unreadCount={unreadCount}
      />

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 mt-12 bg-[var(--glass-scrim)] backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        className={`
          fixed md:static inset-y-0 left-0 z-50
          w-[264px] flex flex-col
          bg-glass-1 backdrop-blur-[28px] border-r border-glass
          transform transition-transform duration-300 ease-in-out
          md:translate-x-0
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isDesktopSidebarHidden ? 'md:hidden' : ''}
          mt-12 md:mt-0
        `}
      >
        <div className="p-5 border-b border-glass">
          <Link
            to="/"
            className="inline-flex shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 rounded-md"
          >
            <KolMyGlassLogo variant="nav" />
          </Link>
          <p className="text-xs uppercase tracking-[0.05em] text-glass-text-2 mt-3">Brand Portal</p>
        </div>

        <PortalNavGroups groups={navGroups} />

        <div className="p-3 border-t border-glass space-y-1">
          <div className="flex items-center gap-2 px-1 pb-2 md:hidden" aria-label="Portal utilities">
            <OpenKanbanButton className="!h-11 !w-11" />
            <ThemeToggle className="!h-11 !w-11" />
          </div>
          <button
            type="button"
            onClick={() => setAppearanceOpen((v) => !v)}
            className={`${navLinkBase} ${navLinkInactive} w-full text-left`}
          >
            <Palette className="w-5 h-5" />
            Appearance
          </button>
          {appearanceOpen ? (
            <div className="px-2 pb-2">
              <AppearanceSettings showAgencyAccent={false} compact />
            </div>
          ) : null}
          <Link to="/brand/profile" className={`${navLinkBase} ${navLinkInactive}`}>
            <User className="w-5 h-5" />
            Profile
          </Link>
          <Link to="/brand/team" className={`${navLinkBase} ${navLinkInactive}`}>
            <UserCog className="w-5 h-5" />
            Team
          </Link>
          <button type="button" onClick={logout} className={`${navLinkBase} ${navLinkInactive} w-full text-left`}>
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      <div className="mt-12 flex flex-1 flex-col overflow-hidden md:mt-0">
        <div className="hidden md:flex h-16 bg-glass-1 backdrop-blur-[28px] border-b border-glass items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsDesktopSidebarHidden(!isDesktopSidebarHidden)}
              className="p-2 text-glass-text-2 hover:text-glass-text hover:bg-glass-3 rounded-lg transition-colors"
              aria-label={isDesktopSidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
              title={isDesktopSidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
            >
              {isDesktopSidebarHidden ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            <h2 className="page-title text-[1.15rem]">{pageTitle}</h2>
          </div>

          <div className="flex items-center gap-3">
            {user && <PortalUserChip user={user} />}
            <OpenKanbanButton />
            <ThemeToggle />
            <Link
              to="/brand/notifications"
              className="relative p-2 text-glass-text-2 hover:text-glass-text hover:bg-glass-3 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-accent2 text-white text-[0.6rem] flex items-center justify-center rounded-full shadow-[0_0_10px_rgb(var(--accent-rgb)/0.6)]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        <div className="flex-1 overflow-auto glass-scroll">
          <div className={portalContentClassName(location.pathname)}>
            <Outlet />
          </div>
        </div>
      </div>
      <div data-portal-overlay-root />
    </div>
  );
}
